// Dev-only fixtures + dispatcher for the BYOK key pool, on BOTH faces: the org
// pool (`/v1/billing/keys*`) and the personal one (`/v1/me/keys*`), which serve
// the same sample set because MyKeys and AccountKeys render the same cards.
// Also `/v1/me/llm-pref`, the two pre-capture enumeration doors (`provider-models-mocks.ts`)
// and the keys' live concurrency (`/v1/billing/capacity`, #6863, `key-capacity-mocks.ts`).
//
// Split out of `mocks.ts` (plan 101 slice 15 step 4). `null` means "not mine"
// and `mockFetch` falls through — so `billingMock`, which runs earlier and owns
// the seat/spend half of `/v1/billing/*`, keeps handing these paths back.
//
// NO `Resource` EXISTS FOR A BYOK KEY. `packages/domain/src/wire/` publishes
// eleven of them and a key is not among them — the pool is projected inline by
// `apps/api/src/routes/billing-keys.ts`, with the secret material masked there.
// So these fixtures are graded by `KeyRow`, the dashboard's own type, which is a
// MIRROR and can therefore drift the way `RunnerRow` did. Named rather than
// papered over: closing it is a change to `packages/domain`, not to this file.

import { BYOK_KEY_PRIORITY_MAX, BYOK_KEY_PRIORITY_MIN } from '@developerz/domain';
import { ApiError } from './api';
import { capacityView, setCapacity } from './key-capacity-mocks';
import type { KeyModelRow, KeyRow } from './keys';
import { at } from './mock-session';
import { providerModelsPage } from './provider-models-mocks';

// The `Page` both faces serve (`next_cursor: null`: each is its whole pool), as SEEDED.
const SEED: { data: KeyRow[]; next_cursor: null } = {
  next_cursor: null,
  data: [
    {
      id: 'key_1',
      provider: 'anthropic',
      model: 'claude-opus-4-8',
      status: 'valid',
      last_verified_iso: at('2026-06-28'),
      last_error: null,
      label: 'anthropic-prod',
      kind: 'metered',
      // Two models at one tier — the shape `byok_key_models` exists for, and
      // what the card's tag list has to render without collapsing them.
      priority: 10,
      capacity: 8,
      purpose: null,
      models: [
        { model: 'claude-opus-4-8', tier: 'heavy', active: true },
        { model: 'claude-sonnet-5', tier: 'heavy', active: true },
        { model: 'claude-haiku-4-5', tier: 'fast', active: true },
      ],
    },
    {
      id: 'key_2',
      provider: 'openai',
      model: 'gpt-5',
      status: 'unverified',
      last_verified_iso: null,
      last_error: null,
      label: null,
      kind: 'metered',
      priority: 100,
      capacity: null,
      purpose: null,
      models: [{ model: 'gpt-5', tier: null, active: true }],
    },
    {
      id: 'key_3',
      provider: 'gemini',
      model: 'gemini-2.5-pro',
      status: 'valid',
      last_verified_iso: at('2026-06-20'),
      last_error: null,
      label: 'gemini-vision',
      kind: 'metered',
      priority: 50,
      capacity: null,
      purpose: null,
      models: [{ model: 'gemini-2.5-pro', tier: 'smart', active: true }],
    },
    {
      // A flat-rate coding plan (Kimi Code) — quota-bounded, so the card shows a
      // Subscription badge and $0 metered spend.
      id: 'key_4',
      provider: 'kimi',
      model: 'kimi-k2',
      status: 'valid',
      last_verified_iso: at('2026-06-22'),
      last_error: null,
      label: 'kimi-coding',
      kind: 'subscription',
      priority: 5,
      capacity: null,
      purpose: null,
      models: [{ model: 'kimi-k2', tier: 'general', active: true }],
    },
    {
      // A refused key, carrying the provider's OWN sentence — the state this
      // preview had no row for, which is why the missing `last_error` projection
      // went unnoticed: an `invalid` card rendered a red badge and nothing else.
      id: 'key_5',
      provider: 'xai',
      model: 'grok-4',
      status: 'invalid',
      last_verified_iso: at('2026-06-11'),
      last_error:
        'model "grok-4" check failed: {"error":"The model grok-4 does not exist or you do not have access to it."}',
      label: 'xai-spare',
      kind: 'metered',
      // Untagged, like every row the backfill wrote for a key captured before
      // tiers existed: present, so the card renders no tag band at all.
      priority: 100,
      capacity: null,
      purpose: null,
      models: [{ model: 'grok-4', tier: null, active: true }],
    },
  ],
};
let keys = structuredClone(SEED); // the live pool, reseeded per test (`mock-state.ts`)
export function resetKeysMocks(): void {
  keys = structuredClone(SEED);
}

/**
 * The 201 a capture answers with. Echoes the catalogue the POST asked for, and
 * applies the server's own rule for an omitted `models`: exactly ONE untagged
 * row equal to the key's `model` (an empty one breaks the card's tag band). Both doors
 * verify inline (plan 102 slice 05): the row AFTER a passing probe, `verification` beside.
 */
function capturedKey(init?: RequestInit): KeyRow & { verification: object } {
  const body = init?.body
    ? (JSON.parse(String(init.body)) as {
        provider?: KeyRow['provider'];
        model?: string;
        models?: readonly Omit<KeyModelRow, 'active'>[];
      })
    : {};
  const model = body.model ?? 'claude-opus-4-8';
  return {
    id: 'key_new',
    provider: body.provider ?? 'anthropic',
    model,
    status: 'valid',
    last_verified_iso: at('2026-07-24'),
    last_error: null,
    label: null,
    kind: null,
    priority: 100, // the column's default — capture takes no priority from this form
    capacity: null,
    purpose: null,
    models: (body.models ?? [{ model, tier: null }]).map((row) => ({ ...row, active: true })),
    verification: { outcome: 'verified', reason: null, fault: null },
  };
}

/** Answer with the pool row after `patch`, or the API's own 404 for an unknown id. */
function patchKey(id: string, patch: (row: KeyRow) => KeyRow): Promise<unknown> {
  keys = { ...keys, data: keys.data.map((k) => (k.id === id ? patch(k) : k)) };
  const row = keys.data.find((k) => k.id === id);
  return row ? Promise.resolve(row) : Promise.reject(new ApiError(404, 'unknown key'));
}

/**
 * Rotate (#971) and verify (#105) both land on `markKeyValid` server-side, so
 * they answer the SAME transition here: flip the sample row to `valid` IN the
 * list, so the follow-up refetch shows what the write claimed.
 *
 * `last_error` goes with the status — both writes are probe-first, so the row
 * they answer with is a key that just PASSED, and the server clears the column
 * in the same write.
 */
function markValid(id: string): Promise<unknown> {
  return patchKey(id, (k) => ({
    ...k,
    status: 'valid',
    last_verified_iso: at('2026-07-24'),
    last_error: null,
  }));
}

/** Re-stamp a key's billing nature (#1247) — the PATCH answers the masked row. */
function setKind(id: string, init?: RequestInit): Promise<unknown> {
  const body = init?.body ? (JSON.parse(String(init.body)) as { kind?: KeyRow['kind'] }) : {};
  return patchKey(id, (k) => ({ ...k, kind: body.kind ?? k.kind }));
}

/**
 * Rename a key's pin label (#5211, epic #5200 slice 03) — `PATCH …/:id/label`
 * (`apps/api/src/routes/key-label.ts`). The mock honours the ONE refusal a
 * single-user dev session can reach on its own — `label_taken`, another key in
 * the ORG pool already holding the name — never `label_in_use` / `label_pinned`,
 * which need pending-work / route-pin awareness this fixture does not model
 * (those refusals are exercised at the component level with a stubbed client).
 */
function setLabel(id: string, init?: RequestInit): Promise<unknown> {
  const body = init?.body ? (JSON.parse(String(init.body)) as { label?: string | null }) : {};
  const label = body.label ?? null;
  if (label !== null && keys.data.some((k) => k.id !== id && k.label === label)) {
    return Promise.reject(
      new ApiError(
        409,
        'label taken',
        undefined,
        'another key in this account already holds that label',
        { reason: 'label_taken' },
      ),
    );
  }
  return patchKey(id, (k) => ({ ...k, label }));
}

/**
 * Re-rank a key (plan 102 slice 05b) — the PATCH answers the masked row. The
 * mock keeps the server's bounds: outside 0..10000 is the 400 the route's Zod
 * schema answers, so a client that skipped its own check sees the real refusal.
 */
function setPriority(id: string, init?: RequestInit): Promise<unknown> {
  const body = init?.body ? (JSON.parse(String(init.body)) as { priority?: unknown }) : {};
  const priority = body.priority;
  if (
    typeof priority !== 'number' ||
    !Number.isInteger(priority) ||
    priority < BYOK_KEY_PRIORITY_MIN ||
    priority > BYOK_KEY_PRIORITY_MAX
  ) {
    // The route's own problem `detail` (`apps/api`, plan 102 slice 05b).
    return Promise.reject(new ApiError(400, 'invalid BYOK priority payload'));
  }
  return patchKey(id, (k) => ({ ...k, priority }));
}

/** The revoked row a DELETE answers with — a tombstone, not a pool member. */
function revoked(id: string): unknown {
  return {
    id,
    provider: 'anthropic',
    model: 'claude-opus-4-8',
    status: 'revoked',
    last_verified_iso: null,
    models: [],
  };
}

/**
 * One pool face — `/v1/billing/keys` or `/v1/me/keys` — under the prefix given.
 * The two faces serve the same sample, so the routing is stated once here
 * rather than twice in the dispatcher.
 *
 * THE DOORS ARE ENUMERATED, not swept up by a prefix catch-all, and that is
 * what closed a gap this split found: the org face previously answered GET,
 * POST, `/rotate` and DELETE only, so `verifyKey` and `updateKeyKind` — both
 * live controls on `/account/keys` — fell through to `Unhandled mock route` and
 * rendered a hard failure under `PUBLIC_MOCKS`. The per-family coverage gate
 * could not see it: `/v1/billing/*` counts as one family and `/v1/billing/usage`
 * answers for it. Anything not listed here still falls through, on purpose.
 */
function pool(
  prefix: string,
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  if (path === prefix) {
    if (method === 'POST') return Promise.resolve(capturedKey(init));
    return method === 'GET' ? Promise.resolve(keys) : null;
  }
  const [id = '', action = ''] = path.slice(`${prefix}/`.length).split('/');
  if (method === 'POST' && action === 'rotate') return markValid(id);
  const pass = { outcome: 'verified', reason: null, fault: null } as const; // a 200 says so: 05-10
  if (method === 'POST' && action === 'verify')
    return markValid(id).then((row) => ({ ...(row as object), ...pass }));
  if (method === 'PATCH' && action === 'kind') return setKind(id, init);
  if (method === 'PATCH' && action === 'priority') return setPriority(id, init);
  if (method === 'PATCH' && action === 'capacity') return setCapacity(id, init, patchKey);
  if (method === 'PATCH' && action === 'label') return setLabel(id, init);
  if (method === 'DELETE' && action === '') return Promise.resolve(revoked(id));
  return null;
}

/** Serve the key pool on both faces plus the model preference; `null` otherwise. */
export function keysMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const bare = path.split('?')[0] ?? path;
  if (bare === '/v1/me/llm-pref') {
    if (method === 'DELETE') return Promise.resolve(undefined);
    return Promise.resolve({ pref: { provider: 'anthropic', model: 'claude-opus-4-8' } });
  }
  // The two enumeration doors, which are NOT one route under two prefixes: the
  // org pool probes `/v1/billing/providers/models` while the personal one
  // probes under its own prefix, so `/v1/billing/keys/models` is nobody's
  // address and stays unserved.
  if (bare === '/v1/billing/providers/models' || bare === '/v1/me/keys/models') {
    return method === 'POST' ? Promise.resolve(providerModelsPage(init)) : null;
  }
  if (bare === '/v1/billing/capacity') return method === 'GET' ? capacityView(keys.data) : null;
  for (const prefix of ['/v1/billing/keys', '/v1/me/keys']) {
    if (bare === prefix || bare.startsWith(`${prefix}/`)) return pool(prefix, bare, method, init);
  }
  return null;
}
